import { DownloadConfigInfoType } from '@op/pages/download/types';
import { getBilibiliVideoList } from '@opm/services';

/**
 * Bilibili 视频列表下载配置
 *
 * 下载策略：单页 page_size = 10000；若 total <= PAGE_SIZE 一次拉完即返；
 * 否则按 ceil(total / PAGE_SIZE) 续拉剩余页，全量合并后返给 downloadPage。
 *
 * 为什么不依赖 downloadPage 的内置分页循环？
 *   @op/pages/download/index.tsx 的 downloadPage 硬编码 pageSize = 1000，
 *   对大数据集会发起 N/1000 次请求（如 50000 条要 50 次），开销过大。
 *
 * 实现要点：
 *   - params.page > 1 时直接返回空：downloadPage 仍会按 ceil(total/1000) 继续循环调用本函数，
 *     这些调用快速返回 list:[]，excel.add 空数组无副作用，规避了重复请求。
 *   - 第一次拉 page=1，按返回的 total 算需续拉页数；total <= PAGE_SIZE 时不进入循环。
 *   - 单页失败时 break 并保留已拉到的部分，避免整体下载失败。
 *
 * columns：与表格列对齐（PRD 6 个指标 + 视频元信息），中英文标题/分区/标签成对导出。
 */

const PAGE_SIZE = 1000;

export const MarketingVideoBilibiliDownload: DownloadConfigInfoType = {
  type: 'page',
  downloadFunc: async (params: any) => {
    // downloadPage 会按 ceil(total/1000) 继续循环调用本函数；非首页直接返空，避免重复请求
    if (params?.page > 1) {
      return { list: [], total: 0 };
    }
    let page = 1;
    // 1. 先拉第一页拿到 total
    let firstRes: any;
    try {
      firstRes = await getBilibiliVideoList({
        data: { ...params, page, page_size: PAGE_SIZE },
      });
    } catch {
      return { list: [], total: 0 };
    }

    const allList: any[] = Array.isArray(firstRes?.list)
      ? [...firstRes.list]
      : [];
    const total = firstRes?.total || 0;
    while (total > allList?.length) {
      page += 1;
      let res: any;
      try {
        res = await getBilibiliVideoList({
          data: { ...params, page, page_size: PAGE_SIZE },
        });
      } catch {
        // 单页失败：保留已拉到的部分，不阻塞整体下载
        break;
      }
      const list = Array.isArray(res?.list) ? res.list : [];
      if (list.length === 0) break;
      allList.push(...list);
    }

    return {
      ...firstRes,
      list: allList,
      total,
    };
  },
  columns: () => {
    return [
      { key: 'video_title', header: 'Video Title (ZH)' },
      { key: 'video_title_en', header: 'Video Title (EN)' },
      { key: 'author_name', header: 'Author' },
      { key: 'channel_name', header: 'Category (ZH)' },
      { key: 'channel_name_en', header: 'Category (EN)' },
      { key: 'video_tag', header: 'Tag (ZH)' },
      { key: 'video_tag_en', header: 'Tag (EN)' },
      { key: 'publish_time', header: 'Publish Time' },
      { key: 'video_url', header: 'Video URL' },
      { key: 'view_count', header: 'Views' },
      { key: 'danmaku_count', header: 'Danmu' },
      { key: 'like_count', header: 'Likes' },
      { key: 'coin_count', header: 'Coins' },
      { key: 'share_count', header: 'Shares' },
      { key: 'favorite_count', header: 'Favorites' },
    ];
  },
};
